import ShareIcon from '@mui/icons-material/Share';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BookCoverCard } from '@/components/dashboard/book-cover-card';
import { CollaborationInviteDialog } from '@/components/dashboard/collaboration-invite-dialog';
import { getDashboardItems } from '@/components/dashboard/get-dashboard-items';
import { NovelDescriptionInput } from '@/components/dashboard/novel-description-input';
import { NovelSelectionGrid } from '@/components/dashboard/novel-selection-grid';
import { NovelSettingsSection } from '@/components/dashboard/novel-settings-section';
import { NovelSelection } from '@/components/novel-selection';
import { PageHeader } from '@/components/page-header';
import { useNovel } from '@/context/novel-context';
import { useDashboardInvitations } from '@/hooks/use-dashboard-invitations';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { getNovelShareUrl } from '@/lib/stories-url';

interface DashboardContentProps {
  inviteToken?: string;
}

export function DashboardContent({ inviteToken }: DashboardContentProps) {
  const { t } = useTranslation();
  const { novel: currentNovel, isLoading: loadingNovel } = useNovel();
  const { getSummaries } = useNovelRepository();
  const { toast } = useToast();
  const selectedNovelId = currentNovel?.id;
  const isAnyLoading = loadingNovel;
  const hasPublicChapter = Object.values(currentNovel?.summaries?.chapters || {}).some((chapter) => chapter.publishStatus === 'public');

  const shareNovel = async () => {
    if (!currentNovel?.publicationId) return;
    const url = getNovelShareUrl(currentNovel.publicationId);
    if (navigator.share) await navigator.share({ title: currentNovel.title, url });
    else {
      await navigator.clipboard.writeText(url);
      toast({ title: t('chapters.publish.link_copied') });
    }
  };

  // Custom hook containing all invitation-handling logic
  const {
    pendingInvite,
    dialogOpen,
    setDialogOpen,
    isProcessing,
    handleAcceptInvite,
    handleDeclineInvite,
  } = useDashboardInvitations(inviteToken);

  const counts = useMemo(
    () => ({
      chapters: getSummaries('chapters').length,
      characters: getSummaries('characters').length,
      locations: getSummaries('locations').length,
      organizations: getSummaries('organizations').length,
      events: getSummaries('events').length,
    }),
    [getSummaries]
  );

  const dashboardItems = useMemo(
    () => getDashboardItems(t, counts, t('dashboard.home.view')),
    [t, counts]
  );

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      <Grid container spacing={3} sx={{ mb: 5 }} alignItems="flex-start">
        <Grid size={selectedNovelId ? { xs: 12, md: 4, lg: 4 } : { xs: 12 }}>
          <PageHeader
            id="dashboard-header-step"
            title={t('dashboard.home.title')}
            description={t('dashboard.home.description')}
            sx={{ mb: 0 }}
          />

          {selectedNovelId && (
            <Box sx={{ mt: 2, display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <Box
                id="active-novel-selector-step"
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 4,
                  background:
                    'linear-gradient(135deg, rgba(161, 130, 74, 0.05) 0%, rgba(161, 130, 74, 0.1) 100%)',
                  border: '1px solid',
                  borderColor: 'primary.light',
                  display: 'inline-flex',
                  alignItems: 'center',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                }}
              >
                <NovelSelection showLabel={false} />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: hasPublicChapter && currentNovel?.publicationId ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                  gap: 1,
                  mt: 1.5,
                  px: 1,
                }}
              >
                {hasPublicChapter && currentNovel?.publicationId && (
                  <Tooltip title={t('chapters.publish.share_novel')}>
                    <Button
                      color="primary"
                      variant="text"
                      startIcon={<ShareIcon />}
                      onClick={shareNovel}
                      aria-label={t('chapters.publish.share_novel')}
                      sx={{ minHeight: 44, px: 2, borderRadius: 2 }}
                    >
                      {t('common.share')}
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title={t('export.open')}>
                  <Button
                    component={Link}
                    to="/export"
                    color="primary"
                    variant="text"
                    startIcon={<FileDownloadOutlinedIcon />}
                    aria-label={t('export.open')}
                    sx={{ minHeight: 44, px: 2, borderRadius: 2 }}
                  >
                    {t('export.action')}
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Grid>

        {selectedNovelId && (
          <Grid
            id="novel-identity-step"
            size={{ xs: 12, md: 8, lg: 8 }}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
              gap: 3,
            }}
          >
            <BookCoverCard />
            <NovelDescriptionInput />
          </Grid>
        )}
      </Grid>

      {!selectedNovelId ? (
        <NovelSelectionGrid />
      ) : (
        <>
          <Grid container spacing={3} id="dashboard-stats-step">
            {dashboardItems.map((item) => (
              <Grid key={item.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Link to={item.href} style={{ textDecoration: 'none' }}>
                  <Card
                    sx={{
                      height: '100%',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px -10px rgba(0,0,0,0.12)',
                        borderColor: 'primary.light',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        pb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{ fontSize: '1.25rem', fontWeight: 600 }}
                      >
                        {item.title}
                      </Typography>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: 'rgba(161, 130, 74, 0.1)',
                        }}
                      >
                        {item.icon}
                      </Box>
                    </Box>
                    <CardContent sx={{ pt: 0 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}
                      >
                        {isAnyLoading && item.count === 0 && !currentNovel ? (
                          <CircularProgress size={28} thickness={5} />
                        ) : (
                          <Typography
                            variant="h1"
                            sx={{
                              fontSize: '2.5rem',
                              fontWeight: 700,
                              color: 'primary.main',
                            }}
                          >
                            {item.count}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.5 }}
                      >
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Link>
              </Grid>
            ))}
          </Grid>

          {/* Novel Settings Section */}
          {currentNovel && <NovelSettingsSection />}
        </>
      )}
      <CollaborationInviteDialog
        open={dialogOpen}
        invite={pendingInvite}
        onClose={() => setDialogOpen(false)}
        onAccept={handleAcceptInvite}
        onDecline={handleDeclineInvite}
        isProcessing={isProcessing}
      />
    </Box>
  );
}
export default DashboardContent;
